import { getEnv } from "../../../utils/env"
import queryString from "query-string"
import {
  getStudentById,
  updateStudentById,
  getEducatorById,
  updateEducatorById,
  getDistrictById,
  getSchoolById
} from "../../mp-database"
import { firebaseAuth, analytics } from "../../mp-database"
import storage, { AUTH_CUSTOM_TOKEN_KEY } from "../../mp-core/helpers/storage"
import {
  EventCategory as EVC,
  EventAction as EVA,
  EventLabel as EVL,
  Roles,
  timestamper,
  postRequest,
  getRequest
} from "../../mp-utils/generic-utils"
import { encryptValue, generateEventMetadata, faLogEvent } from "../../mp-utils/generic-utils/Events/helpers"
import { trackEvent, trackSessionStart } from "../../mp-utils/generic-utils/Events/eventService"

const afterFetchUser = (user, userId, role, collection) => {
  try {
    // I don't believe super admins take this route, but still putting safeguardsto prevent errors
    if (role == Roles.Super_Administrator) {
      analytics.setUserProperties({
        user_id: user.ref.id,
        role: role
      })
    } else {
      // get school and district docs
      Promise.all([
        user.data.school ? getSchoolById(user.data.school) : null,
        user.data.schoolDistrict
          ? getDistrictById(user.data.schoolDistrict)
          : null
      ]).then(([school, district]) => {
        // set user properties
        const analyticsObj = {
          user_id: user.ref.id,
          role: user.data.role,
          state: user.data.state
        }

        if (school) {
          analyticsObj.school = school?.data?.name ?? "unknown"
          analyticsObj.school_sfid = school?.data?.sfid ?? "unknown"
          analyticsObj.school_id = school?.data?.id ?? ""
        }

        if (district) {
          analyticsObj.district = district?.data?.name ?? "unknown"
          analyticsObj.district_sfid = district?.data?.sfid ?? "unknown"
          analyticsObj.district_id = district?.data?.id ?? ""
        }

        analytics.setUserProperties(analyticsObj)
      })
    }

    let label = "unknown"
    switch (role) {
      case Roles.Super_Administrator:
        label = EVL.SUPER_ADMIN
        break
      case Roles.District_Admin:
        label = EVL.DISTRICT_ADMIN
        break
      case Roles.School_Admin:
        label = EVL.SCHOOL_ADMIN
        break
      case Roles.ACT_Admin:
        label = EVL.ACT_ADMIN
        break
      case Roles.ACT_Multi_District_Admin:
        label = EVL.ACT_MULTI_DISTRICT_ADMIN
        break
      case Roles.Sales_Rep:
        label = EVL.SALES_REP
        break
      case Roles.Instructor:
        label = EVL.INSTRUCTOR
        break
      case Roles.Teacher:
        label = EVL.TEACHER
        break
      case Roles.Student:
        label = EVL.STUDENT
        break
      case Roles.Course_Author:
        label = EVL.COURSE_AUTHOR
        break
    }

    faLogEvent(EVC.AUTH, EVA.LOGIN, label)

    trackEvent({
      type: EVA.LOGIN,
      category: EVC.AUTH,
      label,
    }, "fa")

    // Write only the new timestamp — don't spread the existing login object back,
    // because the server may have trimmed it for payload size
    const updateVals = {
      [`activity.login.${timestamper()}`]: true
    }

    return collection === "students"
      ? updateStudentById(user.id || userId, updateVals)
      : updateEducatorById(user.id || userId, updateVals)
  } catch (err) {
    console.log(`Error in afterFetchUser: ${err}`)
  }
}

export const logLogin = (userId, role) => {
  let collection = "students"

  if (role !== Roles.Student) {
    collection = "educators"
    getEducatorById(userId)
      .then(userDoc => afterFetchUser(userDoc, userId, role, collection))
      .catch(err => console.log(err))
  } else {
    getStudentById(userId)
      .then(userDoc => afterFetchUser(userDoc, userId, role, collection))
      .catch(err => console.log(err))
  }
}

export const loginTheUser = ({
  customToken,
  user,
  error,
  isNotRegistered,
  encryptedEmail,
  district,
  selectedDistrict
}) => {
  if (error) {
    console.log(`found error: ${error}`)
    let logObj = {
      email: encryptedEmail,
      method: "email"
    }
    if (district) {
      const { state, name } = selectedDistrict

      logObj = {
        method: "username",
        username: encryptedEmail,
        state,
        district_name: name,
        district_id: district
      }
    }

    // Pentest finding #7: the server no longer says whether the account
    // exists; the missing-account / bad-password split is in its logs.
    if (error === "invalid-credentials") {
      faLogEvent(EVC.AUTH, EVA.LOGIN, EVL.INVALID_CREDENTIALS, logObj)

      trackEvent({
        type: EVA.LOGIN,
        category: EVC.AUTH,
        label: EVL.INVALID_CREDENTIALS,
        meta: logObj,
      }, "fa")
    } else if (error === "not-found") {
      faLogEvent(EVC.AUTH, EVA.LOGIN, EVL.MISSING_ACCOUNT, logObj)

      trackEvent({
        type: EVA.LOGIN,
        category: EVC.AUTH,
        label: EVL.MISSING_ACCOUNT,
        meta: logObj,
      }, "fa")
    } else if (error === "incorrect-password") {
      faLogEvent(EVC.AUTH, EVA.LOGIN, EVL.BAD_PASSWORD, logObj)

      trackEvent({
        type: EVA.LOGIN,
        category: EVC.AUTH,
        label: EVL.BAD_PASSWORD,
        meta: logObj,
      }, "fa")
    } else if (error === "expired-password") {
      faLogEvent(EVC.AUTH, EVA.LOGIN, EVL.EXPIRED_PASSWORD, logObj)

      trackEvent({
        type: EVA.LOGIN,
        category: EVC.AUTH,
        label: EVL.EXPIRED_PASSWORD,
        meta: logObj,
      }, "fa")
    }

    throw error
  }
  return firebaseAuth().signInWithCustomToken(customToken)
}

export const authUser = (
  email,
  password,
  success,
  district,
  selectedDistrict
) => {
  return new Promise((resolve, reject) => {
    if (!email || !password) {
      faLogEvent(EVC.AUTH, EVA.LOGIN, EVL.MISSING_CREDENTIALS, {
        missing_email: !email,
        missing_password: !password
      })

      trackEvent({
        type: EVA.LOGIN,
        category: EVC.AUTH,
        label: EVL.MISSING_CREDENTIALS,

        meta: {
          missing_email: !email,
          missing_password: !password,
        },
      }, "fa")

      return reject("Email/Username or password cannot be blank")
    }

    const data = {
      email,
      password,
      app: getEnv("APPLICATION_ID")
    }
    if (district) data.district = district
    const url = `${getEnv("CLOUD_FUNCTION_ROOT")}/authGroup-authUser?`
    let userData = null
    let encryptedEmail = email
    try {
      encryptedEmail = encryptValue(email)
    } catch (e) {}
    postRequest(url, data)
      .then(res => {
        return JSON.parse(res)
      })
      .then(({ customToken, user, error, passwordReset, isNotRegistered }) => {
        userData = user

        // Add validation for customToken
        if (!customToken || typeof customToken !== "string") {
          console.warn("Invalid customToken received:", {
            customToken,
            error,
            user
          })
          if( error === 'expired-password' ) {
              throw { message: error, passwordReset }
          }
          throw error || "Invalid authentication token"
        }

        storage.set(AUTH_CUSTOM_TOKEN_KEY, customToken)

        return loginTheUser({
          customToken,
          user,
          error,
          isNotRegistered,
          encryptedEmail,
          district,
          selectedDistrict
        })
      })
      .then(authResponse => {
      trackSessionStart({
        method: district ? "username" : "email"
      })

      faLogEvent(EVC.AUTH, EVA.LOGIN, EVL.SUCCESS, {
        user_id: userData.id,
        method: district ? "username" : "email"
      })

      trackEvent({
        type: EVA.LOGIN,
        category: EVC.AUTH,
        label: EVL.SUCCESS,

        meta: {
          method: district ? "username" : "email",
        },
      }, "fa", {
        user_id: userData.id,
      })

      faLogEvent(EVC.AUTH, EVA.LOGIN, EVL.REPORTING, generateEventMetadata({
        userId: userData.id,
        userRole: userData.role,
        eventAction: EVA.LOGIN,
        districtId: userData?.schoolDistrict,
        schoolId: userData?.school
      }))

      trackEvent({
        type: EVA.LOGIN,
        category: EVC.AUTH,
        label: EVL.REPORTING,
      }, "fa", generateEventMetadata({
        userId: userData.id,
        userRole: userData.role,
        eventAction: EVA.LOGIN,
        districtId: userData?.schoolDistrict,
        schoolId: userData?.school,
      }))

      resolve(success(userData))
    })
      .catch(err => {
      let logObj = {
        email: encryptedEmail,
        method: "email"
      }
      if (district) {
        const { state, name } = selectedDistrict

        logObj = {
          method: "username",
          username: encryptedEmail,
          state,
          district_name: name,
          district_id: district
        }
      }

      faLogEvent(EVC.AUTH, EVA.LOGIN, EVL.ERROR, logObj)

      trackEvent({
        type: EVA.LOGIN,
        category: EVC.AUTH,
        label: EVL.ERROR,
        meta: logObj,
      }, "fa")

      reject(err)
    })
  })
}

export const pingAuthEducator = () => {
  // spins up the password cloud function
  const pingUrl = `${getEnv("CLOUD_FUNCTION_ROOT")}/authGroup-authUser?`
  return postRequest(pingUrl, { ping: true }).catch(err => console.log(err))
}

export const getUserInviteById = async invitationId => {
  try {
    const query = queryString.stringify({
      invitationId,
      app: getEnv("APPLICATION_ID")
    })
    const userInvite = await getRequest(
      `${getEnv("CLOUD_FUNCTION_ROOT")}/authGroup-getUserInvite?${query}`
    )

    return JSON.parse(userInvite)
  } catch (error) {
    throw JSON.parse(error)
  }
}

export const registerUser = async (invitation, formData, isStudent) => {
  try {
    const data = {
      invitationId: invitation.id,
      password: formData.password,
      app: getEnv("APPLICATION_ID")
    }

    if (invitation.isBulkInvitation) {
      data.firstName = formData.firstName
      data.lastName = formData.lastName
    }

    const url = `${getEnv(
      "CLOUD_FUNCTION_ROOT"
    )}/authGroup-completeRegistration`

    await postRequest(url, data)

    faLogEvent(EVC.EMAIL, EVA.VERIFY, isStudent ? EVL.STUDENT : EVL.EDUCATOR)

    trackEvent({
      type: EVA.VERIFY,
      category: EVC.EMAIL,
      label: isStudent ? EVL.STUDENT : EVL.EDUCATOR,
    }, "fa")

    faLogEvent(EVC.AUTH, EVA.CREATE, EVL.PASSWORD)

    trackEvent({
      type: EVA.CREATE,
      category: EVC.AUTH,
      label: EVL.PASSWORD,
    }, "fa")
  } catch (error) {
    faLogEvent(EVC.EMAIL, EVA.VERIFY, EVL.ERROR, {
      error: `${error}`
    })

    trackEvent({
      type: EVA.VERIFY,
      category: EVC.EMAIL,
      label: EVL.ERROR,

      meta: {
        error: `${error}`,
      },
    }, "fa")

    throw JSON.parse(error)
  }
}
